import { User } from "../../domain";
import {
  Endpoints,
  RemoteDataSource,
  UserRemoteDataSourceError,
} from "../core";

export class UserRemoteDataSource extends RemoteDataSource {
  public async isRegistered(phone: string): Promise<boolean> {
    try {
      await this.post(Endpoints.login, { body: { phone_number: phone } });
      return true;
    } catch (error) {
      if (!error.response) UserRemoteDataSourceError.networkError();
      return false;
    }
  }

  public async register(
    firstName: string,
    surName: string,
    phone: string,
    password: string,
    email?: string
  ): Promise<string> {
    try {
      const res = await this.post(Endpoints.register, {
        body: {
          first_name: firstName,
          sur_name: surName,
          phone_number: phone,
          password,
          email: email === undefined ? "" : email,
        },
      });
      return res.data.token;
    } catch (error) {
      if (!error.response) throw UserRemoteDataSourceError.networkError();
      const { data } = error.response;
      if (data.phone_number === "The phone number must be unique") {
        throw UserRemoteDataSourceError.phoneAlreadyUsedError();
      }
      if (data.email === "The email must be unique") {
        throw UserRemoteDataSourceError.emailAlreadyUsedError();
      }
      throw UserRemoteDataSourceError.serverError();
    }
  }

  public async numberRegister(phone: string): Promise<void> {
    try {
      await this.post(Endpoints.numberRegister, {
        body: { phone_number: phone },
      });
    } catch (error) {
      if (!error.response) throw UserRemoteDataSourceError.networkError();
      throw UserRemoteDataSourceError.serverError();
    }
  }

  public async loginWithPassword(
    phone: string,
    password: string
  ): Promise<string> {
    try {
      const res = await this.post(Endpoints.loginWithPassword, {
        body: {
          phone_number: phone,
          password,
        },
      });
      return res.data.token;
    } catch (error) {
      if (!error.response) throw UserRemoteDataSourceError.networkError();
      if (error.response.data.password === "The password is wrong") {
        throw UserRemoteDataSourceError.wrongPasswordError();
      }
      throw UserRemoteDataSourceError.serverError();
    }
  }

  public async loginWithCode(phone: string): Promise<void> {
    try {
      await this.post(Endpoints.loginWithCode, {
        body: { phone_number: phone },
      });
    } catch (error) {
      if (!error.response) throw UserRemoteDataSourceError.networkError();
      throw UserRemoteDataSourceError.serverError();
    }
  }

  public async verifyCode(phone: string, code: string): Promise<string> {
    try {
      const res = await this.post(Endpoints.confirmVerificationCode, {
        body: { phone_number: phone, code },
      });
      return res.data?.token;
    } catch (error) {
      if (!error.response) throw UserRemoteDataSourceError.networkError();
      if (error.response.data.code === "The code is wrong") {
        throw UserRemoteDataSourceError.wrongVerificationCodeError();
      }
      throw UserRemoteDataSourceError.serverError();
    }
  }

  public async forgotPassword(phone: string): Promise<void> {
    try {
      await this.post(Endpoints.forgotPassword, {
        body: { phone_number: phone },
      });
    } catch (error) {
      if (!error.response) throw UserRemoteDataSourceError.networkError();
      throw UserRemoteDataSourceError.serverError();
    }
  }

  public async changePassword(
    phone: string,
    password: string,
    token: string
  ): Promise<void> {
    try {
      await this.post(Endpoints.changePassword, {
        body: {
          phone_number: phone,
          password,
        },
        headers: { Authorization: `Token ${token}` },
      });
    } catch (error) {
      if (!error.response) throw UserRemoteDataSourceError.networkError();
      throw UserRemoteDataSourceError.serverError();
    }
  }

  public async edit(
    firstName?: string | undefined,
    surName?: string | undefined,
    phone?: string | undefined,
    password?: string | undefined,
    email?: string | undefined
  ): Promise<void> {
    try {
      await this.post(Endpoints.editUser, {
        body: {
          first_name: firstName === undefined ? "" : firstName,
          sur_name: surName === undefined ? "" : surName,
          phone_number: phone === undefined ? "" : phone,
          password: password === undefined ? "" : password,
          email: email === undefined ? "" : email,
        },
      });
    } catch (error) {
      if (!error.response) throw UserRemoteDataSourceError.networkError();
      throw UserRemoteDataSourceError.serverError();
    }
  }

  public async getUserInfo(): Promise<User> {
    try {
      const res = await this.get(Endpoints.infoUser);
      return new User().deserialize(res.data);
    } catch (error) {
      if (!error.response) throw UserRemoteDataSourceError.networkError();
      throw UserRemoteDataSourceError.serverError();
    }
  }
}
